import { gql } from 'urql'

// === QUERIES ===

export const GET_COMPANIES = gql`
  query GetCompanies($filter: CompanyFilterInput) {
    companies(filter: $filter) {
      items {
        _id
        name
        rut
        alias
        correlative
        scfo_plan
        adminStatus
        createdAt
        entryDate
        usersCount
        deactivatedAt
        exitDate
        deactivatedByEmail
        deactivationReason
        blockedFeatures
        modulePermissions {
          moduleId
          hasAccess
          excludedFeatures
        }
        group {
          groupId
          groupName
        }
      }
      totalCount
      page
      pageSize
      totalPages
    }
  }
`

export const GET_COMPANY = gql`
  query GetCompany($id: String!) {
    company(id: $id) {
      _id
      name
      rut
      alias
      correlative
      createdAt
      entryDate
      plan
      scfo_plan
      group {
        groupId
        groupName
        assignedAt
        assignedBy
      }
      users {
        userId
        companyRole
        systemRoles
        email
        firstName
        lastName
        accessType
      }
      comments
      adminStatus
      deactivatedAt
      exitDate
      deactivatedByEmail
      deactivationReason
      modulePermissions {
        moduleId
        hasAccess
        excludedFeatures
      }
      statusHistory {
        previousStatus
        newStatus
        changedAt
        changedByEmail
        comment
        commentAuthorId
        commentAuthorEmail
        commentedAt
      }
      serviceHistory {
        previousService
        newService
        changedAt
        changedByEmail
        comment
        commentAuthorId
        commentAuthorEmail
        commentedAt
      }
      permissionHistory {
        changedAt
        changedByEmail
        modulesRestricted
        comment
        commentAuthorId
        commentAuthorEmail
        commentedAt
      }
      blockedFeatures
    }
  }
`

export const GET_COMPANY_STATS = gql`
  query GetCompanyStats {
    companyStats {
      totalCompanies
      activeCompanies
      inactiveCompanies
      normalStatus
      rojoStatus
      bloqueadoStatus
    }
  }
`

// === MUTATIONS ===

export const UPDATE_COMPANY_STATUS = gql`
  mutation UpdateCompanyStatus($input: UpdateCompanyStatusInput!) {
    updateCompanyStatus(input: $input) {
      _id
      adminStatus
      deactivatedAt
      exitDate
      deactivatedByEmail
      deactivationReason
    }
  }
`

export const UPDATE_COMPANY = gql`
  mutation UpdateCompany($input: UpdateCompanyInput!) {
    updateCompany(input: $input) {
      _id
      name
      alias
      correlative
      comments
      plan
      scfo_plan
      adminStatus
      entryDate
      deactivatedAt
      deactivatedByEmail
      deactivationReason
      blockedFeatures
    }
  }
`

export const NEXT_COMPANY_CORRELATIVE = gql`
  query NextCompanyCorrelative($kind: String) {
    nextCompanyCorrelative(kind: $kind)
  }
`

export const ADD_USERS_TO_COMPANY = gql`
  mutation AddUsersToCompany($input: AddUsersToCompanyInput!) {
    addUsersToCompany(input: $input) {
      _id
      users {
        userId
        companyRole
        systemRoles
        email
        firstName
        lastName
        accessType
      }
    }
  }
`

export const REMOVE_USERS_FROM_COMPANY = gql`
  mutation RemoveUsersFromCompany($input: RemoveUsersFromCompanyInput!) {
    removeUsersFromCompany(input: $input) {
      _id
      users {
        userId
        companyRole
        systemRoles
        email
        firstName
        lastName
        accessType
      }
    }
  }
`

export const BULK_ADD_USER_TO_COMPANIES = gql`
  mutation BulkAddUserToCompanies($input: BulkAddUserToCompaniesInput!) {
    bulkAddUserToCompanies(input: $input)
  }
`

export const BULK_REMOVE_USER_FROM_COMPANIES = gql`
  mutation BulkRemoveUserFromCompanies($input: BulkRemoveUserFromCompaniesInput!) {
    bulkRemoveUserFromCompanies(input: $input)
  }
`

// Query para obtener empresas de un usuario
export const GET_COMPANIES_BY_USER = gql`
  query GetCompaniesByUserId($userId: String!) {
    companiesByUserId(userId: $userId) {
      _id
      name
      rut
      alias
      plan
      adminStatus
    }
  }
`

// Query para obtener módulos disponibles
export const GET_AVAILABLE_MODULES = gql`
  query GetAvailableModules {
    availableModules {
      id
      name
      features
    }
  }
`

export const UPDATE_COMPANY_HISTORY_COMMENT = gql`
  mutation UpdateCompanyHistoryComment($input: UpdateCompanyHistoryCommentInput!) {
    updateCompanyHistoryComment(input: $input) {
      _id
    }
  }
`

export const DELETE_COMPANY_HISTORY_COMMENT = gql`
  mutation DeleteCompanyHistoryComment($input: DeleteCompanyHistoryCommentInput!) {
    deleteCompanyHistoryComment(input: $input) {
      _id
    }
  }
`

// Mutation para actualizar permisos de módulos de empresa
export const UPDATE_COMPANY_PERMISSIONS = gql`
  mutation UpdateCompanyPermissions($input: UpdateCompanyPermissionsInput!) {
    updateCompanyPermissions(input: $input) {
      _id
      modulePermissions {
        moduleId
        hasAccess
        excludedFeatures
      }
    }
  }
`

// === BULK OPERATIONS ===

export const BULK_UPDATE_COMPANY_BLOCKED_FEATURES = gql`
  mutation BulkUpdateCompanyBlockedFeatures($input: BulkUpdateCompanyBlockedFeaturesInput!) {
    bulkUpdateCompanyBlockedFeatures(input: $input) {
      success
      failed
      message
    }
  }
`

export const BULK_UPDATE_COMPANY_PERMISSIONS = gql`
  mutation BulkUpdateCompanyPermissions($input: BulkUpdateCompanyPermissionsInput!) {
    bulkUpdateCompanyPermissions(input: $input) {
      success
      failed
      message
    }
  }
`

// === NOTIFICACIONES (OPT-OUT) ===

export const GET_COMPANY_ACCOUNTING_CONTACTS = gql`
  query CompanyAccountingContacts($companyId: String!) {
    companyAccountingContacts(companyId: $companyId) {
      email
      name
      phone
      index
      optOutTypes
      optOutReason
      optOutAt
      optOutByEmail
    }
  }
`

export const EXCLUDE_CONTACT_FROM_NOTIFICATIONS = gql`
  mutation ExcludeContactFromNotifications($input: ExcludeContactFromNotificationsInput!) {
    excludeContactFromNotifications(input: $input) {
      email
      name
      phone
      index
      optOutTypes
      optOutReason
      optOutAt
      optOutByEmail
    }
  }
`

export const RESTORE_CONTACT_NOTIFICATIONS = gql`
  mutation RestoreContactNotifications($input: RestoreContactNotificationsInput!) {
    restoreContactNotifications(input: $input) {
      email
      name
      phone
      index
      optOutTypes
      optOutReason
      optOutAt
      optOutByEmail
    }
  }
`
